import { useStore } from '@nanostores/react';
import { useLoaderData, useNavigate } from '@remix-run/react';
import { useEffect, useState } from 'react';
import { clientFetch } from '~/lib/.client/fetch';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { resetRequiresIntegration, requiresIntegration as getRequiresIntegration } from '~/lib/client-github';
import { BOLT_IMPORT_ID } from '~/lib/constants';
import { useGithubStore } from '~/lib/context/github';
import { hydrate, importStore, type APIProjectData, type ProjectData } from '~/lib/projects';
import { importPromise } from '~/lib/projects/importStore';
import { setDescription } from '~/lib/stores/description';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';
import type { Message } from '~/types/chat';
import type { Target } from '~/types/github';
import type { UserData } from '~/types/user';
import { navigateToStackblitzProject } from '~/utils/navigate';
import { prettyEncodeComponent } from '~/utils/prettyEncodeComponent';
import { stripIndents } from '~/utils/stripIndent';
import { unreachable } from '~/utils/unreachable';

interface ImportProjectData extends ProjectData {
  github?: Target;
}

export function useGithubImport() {
  const { target } = useLoaderData<{ target: string; user?: UserData }>();
  const user = useStore(userStore.user);
  const activeOrganization = useStore(userStore.getActiveOrganization());

  const navigate = useNavigate();

  const [ready, setReady] = useState(false);

  const github = useGithubStore();

  const [requiresIntegration] = useState(getRequiresIntegration);

  useEffect(() => {
    const importMessage = stripIndents`
      I'm importing your repository into Bolt. This may take a moment as I set everything up. Once it's ready, you'll be able to explore and interact with your code.

      <boltArtifact id="github-import" title="Importing GitHub Repository">
        <boltAction type="import" target="${target}"></boltAction>
      </boltArtifact>

      I've successfully imported your project. I'm ready to assist you with analyzing and improving your code.
    `;

    const initialMessages: Message[] = [
      {
        id: BOLT_IMPORT_ID,
        type: 'chat',
        role: 'assistant',
        status: 'complete',
        content: importMessage,
        ignore: true,
        merge: false,
        parts: [],
      } satisfies Message,
    ];

    importStore.setKey('messages', initialMessages);

    async function fetchRepo(): Promise<ImportProjectData | { errorMessage: string }> {
      const url = new URL(`/api/import/github`, window.origin);

      if (activeOrganization) {
        url.searchParams.set('owner', activeOrganization.slug);
        url.searchParams.set('provider', activeOrganization.provider);
      }

      if (requiresIntegration) {
        url.searchParams.set('integration', '1');
      }

      const response = await clientFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          target,
        }),
      });

      if (response.status === 404) {
        if (!user) {
          navigate(`/login?next=${prettyEncodeComponent(location.pathname)}`);
        }

        return Promise.reject(new Error(`You don't have access to the repository`));
      } else if (response.status === 204) {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        return await fetchRepo();
      }

      const result = await response.json<APIProjectData | { errorMessage: string }>();

      if ('errorMessage' in result) {
        return result;
      }

      return {
        ...result,
        id: String(result.id),
      };
    }

    const loadingTimeout = setTimeout(() => {
      importStore.setKey('statusMessage', 'We are still retrieving the repository, hang tight.');
    }, 5000);

    fetchRepo()
      .then((response) => {
        if ('errorMessage' in response) {
          importPromise.reject();
          importStore.setKey('statusMessage', response.errorMessage);

          if (requiresIntegration) {
            sendAnalyticsEvent({
              action: AnalyticsAction.Track,
              payload: {
                event: AnalyticsTrackEvent.GithubRepoImportFailed,
                properties: {
                  target,
                },
              },
            });
          }

          return;
        }

        const project = response;

        const { slug } = project;

        if (project.title) {
          setDescription(project.title);
        }

        if (project.github) {
          github.connectToRepo({
            branchName: project.github.branchName,
            repository: project.github.repository,
          });

          sendAnalyticsEvent({
            action: AnalyticsAction.Track,
            payload: {
              event: AnalyticsTrackEvent.GithubRepoImported,
              properties: {
                repositoryOwner: project.github.repository.owner,
                repositoryName: project.github.repository.name,
              },
            },
          });
        } else if (requiresIntegration && import.meta.env.DEV) {
          unreachable('If integration was requested, we should get back a repo');
        }

        resetRequiresIntegration();

        hydrate(project);

        importPromise.resolve();

        importStore.setKey('messages', initialMessages);
        importStore.setKey('statusMessage', '');

        projectStore.updateProject(project, userStore.getActiveOrganization().get());

        navigateToStackblitzProject(slug);

        setReady(true);
      })
      .catch((error) => {
        importPromise.reject();
        importStore.setKey('statusMessage', error.message);
      })
      .finally(() => {
        clearTimeout(loadingTimeout);
      });
  }, []);

  return {
    ready,
    requiresIntegration,
  };
}
